import React, { useEffect, useState } from 'react';
import { Search, ArrowUpDown, History, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { inr } from '@/lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const REASONS = [
  { key: 'restock', label: 'Restock' },
  { key: 'correction', label: 'Correction' },
  { key: 'damage', label: 'Damage / Loss' },
  { key: 'return', label: 'Customer Return' },
  { key: 'manual', label: 'Manual' },
];

export default function AdminInventory() {
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [adjustFor, setAdjustFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [logs, setLogs] = useState(null);
  const [form, setForm] = useState({ mode: 'delta', value: 0, reason: 'restock', note: '' });

  const load = () => {
    api.get('/admin/inventory', { params: { q, low_stock: lowOnly, limit: 100 } })
      .then((r) => setProducts(r.data)).catch(() => setProducts({ items: [] }));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, lowOnly]);

  const loadLogs = (productId) => {
    setHistoryFor(productId);
    setLogs(null);
    api.get('/admin/inventory/logs', { params: { product_id: productId, limit: 50 } })
      .then((r) => setLogs(r.data)).catch(() => setLogs({ items: [] }));
  };

  const openAdjust = (p) => { setAdjustFor(p); setForm({ mode: 'delta', value: 0, reason: 'restock', note: '' }); };

  const submit = async () => {
    try {
      await api.post('/admin/inventory/adjust', {
        product_id: adjustFor.id, mode: form.mode,
        value: Number(form.value || 0), reason: form.reason, note: form.note,
      });
      toast.success('Stock updated');
      setAdjustFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const field = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none';
  return (
    <div>
      <h1 className="text-2xl font-semibold">Inventory</h1>
      <p className="text-sm text-muted-foreground">Live stock levels and audit log of every stock change.</p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-border bg-white px-4"><Search size={15} className="text-muted-foreground" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="w-full bg-transparent py-2.5 text-sm outline-none" data-testid="admin-inventory-search" /></div>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} /> Low stock only</label>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-2 text-xs font-semibold"><RefreshCw size={13} /> Refresh</button>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-white">
        <table className="w-full text-sm" data-testid="admin-inventory-table">
          <thead className="bg-[hsl(var(--gaze-ivory))] text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">Product</th><th className="p-3">SKU</th><th className="p-3">Category</th><th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {products?.items?.map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="p-3"><div className="flex items-center gap-3"><img src={p.images?.[0]} alt="" className="h-10 w-10 rounded-lg object-cover" /><span className="font-medium">{p.name}</span></div></td>
                <td className="p-3 text-xs text-muted-foreground">{p.sku}</td>
                <td className="p-3 text-xs text-muted-foreground">{p.category_name}</td>
                <td className="p-3 font-semibold">{inr(p.price)}</td>
                <td className="p-3"><span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${p.stock === 0 ? 'bg-red-50 text-red-700' : p.stock <= 5 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>{p.stock}</span></td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <button onClick={() => openAdjust(p)} data-testid="admin-inv-adjust" className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary))] px-3 py-1.5 text-xs font-semibold text-white"><ArrowUpDown size={12} /> Adjust</button>
                    <button onClick={() => loadLogs(p.id)} className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold"><History size={12} /> History</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products && products.items.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No products found.</p>}
      </div>

      {/* Adjust dialog */}
      <Dialog open={!!adjustFor} onOpenChange={(v) => !v && setAdjustFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Adjust stock — {adjustFor?.name}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><label className="text-xs font-medium">Mode</label><select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className={field}><option value="delta">Change by (+/-)</option><option value="absolute">Set absolute value</option></select></div>
            <div><label className="text-xs font-medium">{form.mode === 'delta' ? 'Delta' : 'New stock'}</label><input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={field} data-testid="admin-inv-value" /></div>
            <div><label className="text-xs font-medium">Reason</label><select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={field}>{REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}</select></div>
            <div><label className="text-xs font-medium">Note</label><textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={field} placeholder="Optional note…" /></div>
            <p className="text-xs text-muted-foreground">Current stock: <span className="font-semibold">{adjustFor?.stock}</span>. {form.mode === 'delta' ? `Will become ${Math.max(0, (adjustFor?.stock || 0) + Number(form.value || 0))}` : `Will become ${Math.max(0, Number(form.value || 0))}`}.</p>
            <button onClick={submit} data-testid="admin-inv-save" className="mt-2 w-full rounded-full bg-[hsl(var(--primary))] py-2.5 text-sm font-semibold text-white">Apply</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Stock history</DialogTitle></DialogHeader>
          {!logs ? <p className="text-sm text-muted-foreground">Loading…</p> : logs.items.length === 0 ? <p className="text-sm text-muted-foreground">No stock changes recorded yet.</p> : (
            <div className="space-y-2 text-sm">
              {logs.items.map((l) => (
                <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-2 last:border-0">
                  <div>
                    <p className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()} · <span className="font-semibold uppercase text-foreground">{l.reason}</span>{l.reference ? ` · ${l.reference}` : ''}</p>
                    {l.note && <p className="text-xs">{l.note}</p>}
                  </div>
                  <div className="text-right">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-bold ${l.delta > 0 ? 'bg-emerald-50 text-emerald-700' : l.delta < 0 ? 'bg-red-50 text-red-700' : 'bg-gray-50'}`}>{l.delta > 0 ? '+' : ''}{l.delta}</span>
                    <p className="text-[10px] text-muted-foreground">{l.stock_before} → {l.stock_after}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => setHistoryFor(null)} className="mt-3 inline-flex items-center gap-1 text-sm text-muted-foreground"><X size={13} /> Close</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
